import {
  type FC, useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { mapSettingsColumnsFilterSetGroup } from '~/store/mapSettings/columnsFilter/mapSettingsColumnsFilter.actionCreators';
import { EXPORT_LEGEND_COMPONENT_ID } from '../_shared/types/exportImage/exportImage.constants';
import {
  type ActiveGroupFilters, type GroupingColumnValues,
} from '../_shared/types/grouping/grouping';
import { getActiveGroupFiltersFromFiltersState } from '../_shared/utils/grouping/grouping.helpers';
import {
  useIsMapSettingsSyncInProgressSelector,
  useIsSpreadsheetDataFetchingInProgressSelector,
} from '../store/frontendState/processing/processing.selectors';
import { useMapSettingsColumnsFilterSelector } from '../store/mapSettings/columnsFilter/mapSettingsColumnsFilters.selectors';
import { useMapSettingsGroupingActiveGroupColumnsSelector } from '../store/mapSettings/grouping/mapSettingsGrouping.selectors';
import { useMapSettingsExportImageSettingsSelector } from '../store/mapSettings/toolsState/exportImageSettings/exportImageSettings.selectors';
import { useSpreadsheetColumns } from '../store/matchupData/matchupDataSelectors.hook';
import { GroupingSimpleLegendComponent } from './groupingSimpleLegend.component';
import { useGroupColumnData } from './useGroupColumnData';

export const GroupingSimpleLegendContainer: FC = () => {
  const dispatch = useDispatch();
  const activeColumns = useMapSettingsGroupingActiveGroupColumnsSelector();
  const columnsFilter = useMapSettingsColumnsFilterSelector();
  const isMapSettingsSyncInProgress = useIsMapSettingsSyncInProgressSelector();
  const isSpreadsheetDataLoading = useIsSpreadsheetDataFetchingInProgressSelector();
  const exportImageSettings = useMapSettingsExportImageSettingsSelector();

  const isExportImageMode = !!exportImageSettings.mode;
  const legendMode = exportImageSettings.legendMode;

  const spreadSheetColumns = useSpreadsheetColumns();
  const { isLoading } = useGroupColumnData();

  const activeFilter = useMemo<ActiveGroupFilters>(() => {
    return getActiveGroupFiltersFromFiltersState(columnsFilter);
  }, [columnsFilter]);

  const onFilterChange = useCallback((
    spreadsheetColumnId: SpreadsheetColumnId,
    groups: GroupingColumnValues<1>,
  ) => {
    dispatch(mapSettingsColumnsFilterSetGroup(spreadsheetColumnId, groups));
  }, [dispatch]);

  return (
    <GroupingSimpleLegendComponent
      activeColumns={activeColumns}
      columns={spreadSheetColumns}
      filterValues={activeFilter}
      htmlElementId={legendMode ? EXPORT_LEGEND_COMPONENT_ID : undefined}
      isFilteringDisabled={isExportImageMode}
      isLoading={isLoading || isSpreadsheetDataLoading || isMapSettingsSyncInProgress}
      onFilterChange={onFilterChange}
    />
  );
};
